import _ from "lodash";
import { call, delay, put, select, takeLatest } from "redux-saga/effects";
import { setRevenue } from "../actions";
import { Auth } from "../utilities/auth";
import axios from "../utilities/axiosWrapper";

// Auth reducer
export const selectAuthReducer = (state: any) => state.authReducer;

/**
 * @desc The axios wrapper returns the error object instead of throwing, so on a
 * non 2xx the status and body sit under `response`.
 * @param {*} jsonResponse
 * @return {*} {{ status: number, body: any }}
 */
const readResponse = (jsonResponse: any): { status: number; body: any } => ({
  status: jsonResponse?.status ?? jsonResponse?.response?.status ?? 0,
  body: jsonResponse?.data ?? jsonResponse?.response?.data ?? {},
});

/**
 * @param {*} body
 * @return {string} the API's message, or a generic fallback
 */
const readErrorMessage = (body: any): string =>
  body?.error?.message ?? "Something went wrong. Please try again.";

/**
 * @desc Calls the invoice service and returns the response data, or throws the
 * message to show.
 * @param {string} method
 * @param {string} path without the access token
 * @param {*} data request body
 * @return {*} {Generator}
 */
function* request(method: string, path: string, data: any = {}): Generator {
  const selectedAuthReducer: any = yield select(selectAuthReducer);
  const separator = path.includes("?") ? "&" : "?";
  const params: any = {
    method,
    path: `${path}${separator}at=${selectedAuthReducer.accessToken}`,
    data,
  };
  const apikeys: any = yield Auth.getAuthTokens();
  const jsonResponse: any = yield call(axios, params, apikeys);
  const { status, body } = readResponse(jsonResponse);
  if (status >= 200 && status < 300) {
    return _.get(body, "data", null);
  }
  throw new Error(readErrorMessage(body));
}

/**
 * @desc Like request, but returns the status and the body whatever the status,
 * for the sweep steps: a refused step still sends back the sweep as it now
 * stands.
 * @param {string} method
 * @param {string} path
 * @param {*} data
 * @return {*} {Generator}
 */
function* requestWithStatus(method: string, path: string, data: any = {}): Generator {
  const selectedAuthReducer: any = yield select(selectAuthReducer);
  const separator = path.includes("?") ? "&" : "?";
  const params: any = {
    method,
    path: `${path}${separator}at=${selectedAuthReducer.accessToken}`,
    data,
  };
  const apikeys: any = yield Auth.getAuthTokens();
  const jsonResponse: any = yield call(axios, params, apikeys);
  return readResponse(jsonResponse);
}

const messageOf = (ex: any, fallback: string): string =>
  ex?.message && ex.message !== "Network Error" ? ex.message : fallback;

/**
 * @desc Revenue per month, from November 2026 to the current month.
 * @return {*}  {Generator}
 */
export function* fetchRevenueMonthsWorker(): Generator {
  try {
    const months: any = yield call(request, "get", "businesses/revenue?view=months");
    yield put(setRevenue({ months, monthsLoading: false }));
  } catch (ex) {
    yield put(
      setRevenue({
        monthsLoading: false,
        monthsError: messageOf(ex, "Could not read the revenue per month."),
      })
    );
  }
}

/**
 * @desc Revenue per day of one month.
 * @param {*} action
 * @return {*}  {Generator}
 */
export function* fetchRevenueDaysWorker(action: any): Generator {
  try {
    const days: any = yield call(
      request,
      "get",
      `businesses/revenue?view=days&month=${encodeURIComponent(action.data)}`
    );
    yield put(setRevenue({ days, daysLoading: false }));
  } catch (ex) {
    yield put(
      setRevenue({
        daysLoading: false,
        daysError: messageOf(ex, "Could not read the revenue per day."),
      })
    );
  }
}

/**
 * @desc Revenue per day between two dates.
 * @param {*} action
 * @return {*}  {Generator}
 */
export function* fetchRevenueRangeWorker(action: any): Generator {
  const { from, to } = action.data;
  try {
    const range: any = yield call(
      request,
      "get",
      `businesses/revenue?view=range&from=${from}&to=${to}`
    );
    yield put(setRevenue({ range, rangeLoading: false }));
  } catch (ex) {
    yield put(
      setRevenue({
        rangeLoading: false,
        rangeError: messageOf(ex, "Could not read the revenue for these dates."),
      })
    );
  }
}

/**
 * @desc Aya Master's Return bucket per month, and every move out of it.
 * @return {*}  {Generator}
 */
export function* fetchRevenueMasterWorker(): Generator {
  try {
    const master: any = yield call(request, "get", "businesses/revenue?view=master");
    yield put(setRevenue({ master, masterLoading: false }));
  } catch (ex) {
    yield put(
      setRevenue({
        masterLoading: false,
        masterError: messageOf(ex, "Could not read Aya Master."),
      })
    );
  }
}

/**
 * @desc The sweeps prepared so far.
 * @return {*}  {Generator}
 */
export function* fetchRevenueSweepsWorker(): Generator {
  try {
    const sweeps: any = yield call(request, "get", "businesses/revenue/sweeps");
    yield put(setRevenue({ sweeps: sweeps || [], sweepsLoading: false }));
  } catch (ex) {
    yield put(
      setRevenue({ sweepsLoading: false, sweepsError: messageOf(ex, "Could not read the sweeps.") })
    );
  }
}

/**
 * @desc The prepay tax payouts.
 * @return {*}  {Generator}
 */
export function* fetchPrepayTaxPayoutsWorker(): Generator {
  try {
    const prepayPayouts: any = yield call(request, "get", "businesses/revenue/sweeps?source=PrepaySpendTax");
    yield put(setRevenue({ prepayPayouts: prepayPayouts || [], prepayPayoutsLoading: false }));
  } catch (ex) {
    yield put(
      setRevenue({
        prepayPayoutsLoading: false,
        prepayPayoutsError: messageOf(ex, "Could not read the payouts."),
      })
    );
  }
}

/**
 * @desc Where a sweep can go.
 * @return {*}  {Generator}
 */
export function* fetchRevenueSweepAccountsWorker(): Generator {
  try {
    const sweepAccounts: any = yield call(request, "get", "businesses/revenue/sweeps?view=accounts");
    yield put(setRevenue({ sweepAccounts }));
  } catch (ex) {
    yield put(
      setRevenue({ sweepAccountsError: messageOf(ex, "Could not read the DC Bank accounts.") })
    );
  }
}

const sweepScopeQuery = (scope: any): string =>
  [
    `source=${encodeURIComponent(scope.source)}`,
    `kind=${encodeURIComponent(scope.kind)}`,
    `provinces=${encodeURIComponent((scope.provinces || []).join(","))}`,
    `feeGroups=${encodeURIComponent((scope.feeGroups || []).join(","))}`,
    `components=${encodeURIComponent((scope.components || []).join(","))}`,
    `from=${scope.from}`,
    `to=${scope.to}`,
    `leftOut=${encodeURIComponent((scope.leftOut || []).join(","))}`,
  ].join("&");

/**
 * @desc What a sweep of these days would move.
 * @param {*} action
 * @return {*}  {Generator}
 */
export function* previewRevenueSweepWorker(action: any): Generator {
  try {
    const sweepPreview: any = yield call(
      request,
      "get",
      `businesses/revenue/sweeps?view=preview&${sweepScopeQuery(action.data)}`
    );
    yield put(setRevenue({ sweepPreview, sweepPreviewLoading: false }));
  } catch (ex) {
    yield put(
      setRevenue({
        sweepPreview: null,
        sweepPreviewLoading: false,
        sweepPreviewError: messageOf(ex, "Could not work out the amounts."),
      })
    );
  }
}

/**
 * @desc Prepare a sweep, then read the list again.
 * @param {*} action
 * @return {*}  {Generator}
 */
export function* prepareRevenueSweepWorker(action: any): Generator {
  try {
    const { status, body }: any = yield call(
      requestWithStatus,
      "post",
      "businesses/revenue/sweeps",
      action.data
    );
    if (status >= 200 && status < 300) {
      const sweep: any = _.get(body, "data", null);
      yield put(
        setRevenue({
          selectedSweep: sweep,
          sweepPreview: null,
          sweepRunning: false,
          sweepDone: `${sweep?.sweepNumber} is prepared. Nothing has moved yet: check it, then move the funds.`,
        })
      );
    } else {
      yield put(setRevenue({ sweepRunning: false, sweepError: readErrorMessage(body) }));
    }
  } catch (ex) {
    yield put(
      setRevenue({ sweepRunning: false, sweepError: messageOf(ex, "The sweep was not prepared.") })
    );
  }
  yield put({ type: "FETCH_REVENUE_SWEEPS" });
  if (action.data?.source === "PrepaySpendTax") {
    yield put({ type: "FETCH_PREPAY_TAX_PAYOUTS" });
  }
}

/**
 * @desc One sweep with its lines.
 * @param {*} action
 * @return {*}  {Generator}
 */
export function* fetchRevenueSweepWorker(action: any): Generator {
  try {
    const selectedSweep: any = yield call(
      request,
      "get",
      `businesses/revenue/sweeps/${encodeURIComponent(action.data)}`
    );
    yield put(setRevenue({ selectedSweep }));
  } catch (ex) {
    yield put(setRevenue({ sweepError: messageOf(ex, "Could not read the sweep.") }));
  }
}

const SWEEP_DONE_MESSAGES: Record<string, string> = {
  send: "The funds were moved.",
  continue: "The funds were moved.",
  retry: "DC Bank has the transfer: the sweep is sent.",
  resolve: "The sweep is resolved.",
  cancel: "The sweep is cancelled and its invoices can be moved again.",
};

/**
 * @desc One step on a sweep. A refused step comes back with the sweep as it
 * now stands, so the screen shows what happened.
 * @param {*} action
 * @return {*}  {Generator}
 */
/**
 * A business tax bucket sweep is sent in turns, one DC Bank call per business
 * wallet: while it answers Sending, the screen asks for the next turn.
 *
 * @param {*} sweep
 * @return {*} {string} the progress to show, or "" when it is not being sent
 */
const sweepTurnProgress = (sweep: any): string => {
  if (
    sweep?.status !== "Sending" ||
    (sweep?.source !== "BusinessTaxBucket" && sweep?.source !== "PrepaySpendTax")
  ) {
    return "";
  }
  const debited: Record<string, boolean> = {};
  (sweep.lines || []).forEach((line: any) => {
    if (line.releasedAt || !line.businessGuid) {
      return;
    }
    const key = line.businessGuid;
    debited[key] = (debited[key] ?? true) && Boolean(line.debitedAt);
  });
  const businesses = Object.keys(debited).length;
  const done = Object.values(debited).filter(Boolean).length;
  return done < businesses
    ? `Sending ${sweep.sweepNumber}: ${done} of ${businesses} business wallets debited at DC Bank. Keep this page open until it is finished.`
    : `Sending ${sweep.sweepNumber}: every business wallet is debited, moving the total to ${sweep.destinationAccountName}. Keep this page open.`;
};

export function* runRevenueSweepActionWorker(action: any): Generator {
  const { guid, action: step, moved, note } = action.data;
  let nextTurn = false;
  try {
    const { status, body }: any = yield call(
      requestWithStatus,
      "post",
      `businesses/revenue/sweeps/${encodeURIComponent(guid)}`,
      { action: step, moved: Boolean(moved), note: note || "" }
    );
    if (status >= 200 && status < 300) {
      const sweep: any = _.get(body, "data", null);
      const progress = sweepTurnProgress(sweep);
      nextTurn = progress !== "";
      yield put(
        setRevenue({
          selectedSweep: sweep,
          sweepRunning: nextTurn,
          sweepDone: progress || SWEEP_DONE_MESSAGES[step] || "Done.",
        })
      );
    } else {
      const sweep: any = _.get(body, "data.sweep", null);
      yield put(
        setRevenue({
          ...(sweep ? { selectedSweep: sweep } : {}),
          sweepRunning: false,
          sweepError: readErrorMessage(body),
        })
      );
    }
  } catch (ex) {
    yield put(
      setRevenue({
        sweepRunning: false,
        sweepError: messageOf(ex, "No answer. Open the sweep again before doing anything else."),
      })
    );
  }
  const current: any = yield select((rootState: any) => rootState.revenueReducer?.selectedSweep);
  if (current?.source === "PrepaySpendTax") {
    yield put({ type: "FETCH_PREPAY_TAX_PAYOUTS" });
  } else {
    yield put({ type: "FETCH_REVENUE_SWEEPS" });
    yield put({ type: "FETCH_REVENUE_MASTER" });
  }
  if (nextTurn) {
    yield delay(1000);
    yield put({ type: "RUN_REVENUE_SWEEP_ACTION", data: { guid, action: "continue" } });
  }
}

/**
 * @desc The invoices settled on the given days.
 * @param {*} action
 * @return {*}  {Generator}
 */
export function* fetchRevenueInvoicesWorker(action: any): Generator {
  const { from, to } = action.data;
  try {
    const invoices: any = yield call(
      request,
      "get",
      `businesses/revenue/invoices?from=${from}&to=${to}`
    );
    yield put(setRevenue({ invoices, invoicesLoading: false }));
  } catch (ex) {
    yield put(
      setRevenue({
        invoicesLoading: false,
        invoicesError: messageOf(ex, "Could not read the invoices."),
      })
    );
  }
}

/**
 * @desc The invoices not settled yet.
 * @return {*}  {Generator}
 */
export function* fetchRevenuePendingWorker(): Generator {
  try {
    const pending: any = yield call(request, "get", "businesses/revenue/invoices?status=pending");
    yield put(setRevenue({ pending, pendingLoading: false }));
  } catch (ex) {
    yield put(
      setRevenue({
        pendingLoading: false,
        pendingError: messageOf(ex, "Could not read the pending invoices."),
      })
    );
  }
}

/**
 * @desc One invoice in detail.
 * @param {*} action
 * @return {*}  {Generator}
 */
export function* fetchRevenueInvoiceWorker(action: any): Generator {
  try {
    const selected: any = yield call(
      request,
      "get",
      `businesses/revenue/invoices/${encodeURIComponent(action.data)}`
    );
    yield put(setRevenue({ selected, selectedLoading: false }));
  } catch (ex) {
    yield put(
      setRevenue({
        selectedLoading: false,
        selectedError: messageOf(ex, "Could not read the invoice."),
      })
    );
  }
}

/**
 * @desc Record a collection flag, then read the list it came from again so
 * its totals follow.
 * @param {*} action
 * @return {*}  {Generator}
 */
export function* flagRevenueInvoiceWorker(action: any): Generator {
  const { invoiceNumber, status, note, refresh } = action.data;
  try {
    const selected: any = yield call(
      request,
      "post",
      `businesses/revenue/invoices/${encodeURIComponent(invoiceNumber)}`,
      { status, note }
    );
    yield put(
      setRevenue({
        selected,
        actionRunning: false,
        actionDone:
          status === "Collected"
            ? `${invoiceNumber} is flagged as collected.`
            : `${invoiceNumber} is flagged as left to collect.`,
      })
    );
    if (refresh) {
      yield put(refresh);
    }
  } catch (ex) {
    yield put(
      setRevenue({
        actionRunning: false,
        actionError: messageOf(ex, "The flag was not saved. Please try again."),
      })
    );
  }
}

function* fetchRevenueMonthsWatcher(): Generator {
  yield takeLatest("FETCH_REVENUE_MONTHS", fetchRevenueMonthsWorker);
}

function* fetchRevenueDaysWatcher(): Generator {
  yield takeLatest("FETCH_REVENUE_DAYS", fetchRevenueDaysWorker);
}

function* fetchRevenueInvoicesWatcher(): Generator {
  yield takeLatest("FETCH_REVENUE_INVOICES", fetchRevenueInvoicesWorker);
}

function* fetchRevenuePendingWatcher(): Generator {
  yield takeLatest("FETCH_REVENUE_PENDING", fetchRevenuePendingWorker);
}

function* fetchRevenueInvoiceWatcher(): Generator {
  yield takeLatest("FETCH_REVENUE_INVOICE", fetchRevenueInvoiceWorker);
}

function* flagRevenueInvoiceWatcher(): Generator {
  yield takeLatest("FLAG_REVENUE_INVOICE", flagRevenueInvoiceWorker);
}

function* fetchRevenueSweepsWatcher(): Generator {
  yield takeLatest("FETCH_REVENUE_SWEEPS", fetchRevenueSweepsWorker);
}

function* fetchPrepayTaxPayoutsWatcher(): Generator {
  yield takeLatest("FETCH_PREPAY_TAX_PAYOUTS", fetchPrepayTaxPayoutsWorker);
}

function* fetchRevenueSweepAccountsWatcher(): Generator {
  yield takeLatest("FETCH_REVENUE_SWEEP_ACCOUNTS", fetchRevenueSweepAccountsWorker);
}

function* previewRevenueSweepWatcher(): Generator {
  yield takeLatest("PREVIEW_REVENUE_SWEEP", previewRevenueSweepWorker);
}

function* prepareRevenueSweepWatcher(): Generator {
  yield takeLatest("PREPARE_REVENUE_SWEEP", prepareRevenueSweepWorker);
}

function* fetchRevenueSweepWatcher(): Generator {
  yield takeLatest("FETCH_REVENUE_SWEEP", fetchRevenueSweepWorker);
}

function* runRevenueSweepActionWatcher(): Generator {
  yield takeLatest("RUN_REVENUE_SWEEP_ACTION", runRevenueSweepActionWorker);
}

function* fetchRevenueMasterWatcher(): Generator {
  yield takeLatest("FETCH_REVENUE_MASTER", fetchRevenueMasterWorker);
}

function* fetchRevenueRangeWatcher(): Generator {
  yield takeLatest("FETCH_REVENUE_RANGE", fetchRevenueRangeWorker);
}

export {
  fetchRevenueMonthsWatcher,
  fetchRevenueDaysWatcher,
  fetchRevenueInvoicesWatcher,
  fetchRevenuePendingWatcher,
  fetchRevenueInvoiceWatcher,
  flagRevenueInvoiceWatcher,
  fetchRevenueRangeWatcher,
  fetchRevenueMasterWatcher,
  fetchRevenueSweepsWatcher,
  fetchPrepayTaxPayoutsWatcher,
  fetchRevenueSweepAccountsWatcher,
  previewRevenueSweepWatcher,
  prepareRevenueSweepWatcher,
  fetchRevenueSweepWatcher,
  runRevenueSweepActionWatcher,
};
